import { Maximize2, Minimize2, Minus, Plus, Zap } from "lucide-react";
import { ROLE_OPTIONS } from "./personaRoles";

/**
 * Always-visible bottom action toolbar — sits below the input bar.
 * Designed to be thumb-accessible on mobile and uncrowded on desktop.
 */
export function ChatbotFooter({
  step,
  messageCount,
  expanded,
  rolePickerOpen,
  role,
  onNewSession,
  onToggleRolePicker,
  onRoleChange,
  onToggleExpand,
  onMinimize,
}) {
  // New is only relevant once a conversation has started.
  const showNew = step === "chat" && messageCount > 0;

  return (
    <div className="border-t border-rp-border bg-rp-bg shrink-0" data-testid="chatbot-footer">
      {/* Inline role drawer — collapsible, opens above the toolbar */}
      {rolePickerOpen && (
        <div id="chatbot-footer-role-drawer" className="px-3 py-2.5 border-b border-rp-border bg-rp-surface/50" data-testid="chatbot-footer-role-drawer">
          <div className="text-[10px] uppercase tracking-wider text-rp-textFaint mb-1.5 font-mono">
            Your role &mdash; the picker will recommend the right mentor
          </div>
          <div className="flex flex-wrap gap-1">
            {ROLE_OPTIONS.map((r) => {
              const isActive = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onRoleChange(isActive ? null : r.id)}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                    isActive
                      ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-300"
                      : "border-rp-border bg-rp-bg text-rp-textDim hover:border-rp-textFaint hover:text-rp-text"
                  }`}
                  data-testid={`chatbot-footer-role-${r.id}`}
                  aria-pressed={isActive}
                >
                  <span className="mr-0.5">{r.icon}</span>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Action toolbar — 4 buttons, evenly spaced, icon-only on narrow widths */}
      <div className="flex items-stretch divide-x divide-rp-border" data-testid="chatbot-footer-toolbar">
        {showNew ? (
          <ToolbarButton
            onClick={onNewSession}
            icon={<Plus className="h-4 w-4" />}
            label="New"
            title="Start a new chat — current conversation cleared"
            testid="chatbot-new-session"
          />
        ) : (
          <ToolbarPlaceholder />
        )}
        <ToolbarButton
          onClick={onToggleRolePicker}
          icon={<Zap className="h-4 w-4" />}
          label="Role"
          title={role ? `Current role: ${ROLE_OPTIONS.find((r) => r.id === role)?.label}` : "Tell us your role"}
          testid="chatbot-toggle-role"
          active={rolePickerOpen || !!role}
          ariaExpanded={rolePickerOpen}
          ariaControls="chatbot-footer-role-drawer"
        />
        <ToolbarButton
          onClick={onToggleExpand}
          icon={expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          label={expanded ? "Shrink" : "Expand"}
          title={expanded ? "Shrink back to bubble" : "Expand to full screen"}
          testid="chatbot-toggle-expand"
        />
        <ToolbarButton
          onClick={onMinimize}
          icon={<Minus className="h-4 w-4" />}
          label="Minimize"
          title="Minimize — your conversation is saved"
          testid="chatbot-close"
        />
      </div>

      <div className="px-3 py-1.5 text-[10px] text-rp-textFaint text-center border-t border-rp-border/60">
        AI responses are guidance, not legal/regulatory advice.
      </div>
    </div>
  );
}

function ToolbarButton({ onClick, icon, label, title, testid, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testid}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
        active
          ? "text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10"
          : "text-rp-textDim hover:text-rp-text hover:bg-rp-surface"
      }`}
    >
      {icon}
      <span className="hidden min-[360px]:inline">{label}</span>
    </button>
  );
}

function ToolbarPlaceholder() {
  // Keeps the 4-cell grid stable when New is hidden (picker step).
  // `pointer-events-none` avoids the dead clickable zone created by the
  // divide-x divider on the placeholder side.
  return <div className="flex-1 pointer-events-none" aria-hidden="true" />;
}
